import { useAppConfig } from '@plato/app-core/hooks/use-app-config';
import { LinkText } from '@plato/ui-legacy/components/ui/link-text';
import { Notice } from '@plato/ui-legacy/components/ui/notice';
import { EyeIcon } from 'lucide-react';
import { useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { api } from '../../../utils/api.ts';
import { getFullName } from '../../../utils/name.ts';

type Props = {
    ownerId: string;
    onBackToMyCalendar: () => void;
    className?: string;
};

export function ViewingUserBanner({ ownerId, onBackToMyCalendar, className }: Props) {
    const { t } = useTranslation('events');
    const { userId } = useAppConfig();

    const { data: subordinates } = api.platoUser.query.users.getManyUsers.getSubordinates.useQuery();

    const userName = useMemo(() => {
        // Don't show banner when viewing own calendar
        if (!ownerId || ownerId === userId || !subordinates) {
            return null;
        }
        const user = subordinates.find((u) => u.id === ownerId);
        return user ? getFullName(user.firstName, user.lastName) : null;
    }, [ownerId, userId, subordinates]);

    if (!userName) {
        return null;
    }

    return (
        <Notice
            variant="info"
            icon={EyeIcon}
            className={className}
            role="status"
            aria-label={t('viewing_banner_text', { name: userName })}
            description={
                <Trans
                    i18nKey="viewing_banner_text"
                    ns="events"
                    values={{ name: userName }}
                    components={{ strong: <strong className="font-semibold" /> }}
                />
            }
            action={
                <LinkText as="button" onClick={onBackToMyCalendar}>
                    {t('viewing_banner_back')}
                </LinkText>
            }
        />
    );
}
